import { ActivityIcon } from 'components/Icon';
import { BlockActions } from '../BlockActions';
import { BlockDetails } from '../types';
import type { EntityContextType } from 'components';
import { actionErrorAndThrow } from 'utilities';
import { AttachmentButton } from './PlanAttachment';
import { ReactElement } from 'react';
import { BriefButton } from './Brief';
import { CampaignDetails } from 'modules/campaign/campaign/types';
import { StatusSelector } from 'modules/campaign/status/BlockStatusSelector';
import { CommentDialogToggle } from 'modules/comments';
import { AvatarGroup, MenuItem, MenuList, Badge as BBadge, PopoverRoot, PopoverTrigger, PopoverPopup } from '@ff-it/ui';
import { NavLink } from 'react-router-dom';
import { CampaignMenu } from 'modules/campaign/campaign/CampaingMenu';
import { DeadlineDate } from 'components/DeadlineDate';
import { UserAvatar } from 'components/UserAvatar';
import { PeriodWidget } from 'components/PeriodWidget';
import { Header } from 'components/Header';
import { useNotificationSeen } from 'modules/notifications';
import { useRequestHandler } from '../BlockProvider';
import { useBlockRealtime } from '../BlockRealtimeProvider';
import { ChannelSubscribers } from 'components/RealtimeStatus';

export function BlockHeader({
  ctx: { item: block, mutate },
  endpoint,
  campaign,
}: {
  ctx: EntityContextType<BlockDetails>;
  endpoint: string;
  campaign: CampaignDetails;
}): ReactElement {
  const { status, available_statuses, type, title, date_from, date_to, deadline, planners, task_list } = block;
  const requestHandler = useRequestHandler();
  const { channelKey } = useBlockRealtime();
  useNotificationSeen(`campaign:block:${block.id}`);

  return (
    <Header
      heading={
        <>
          <ActivityIcon type={type} /> {title}
        </>
      }
      attributes={
        <Header.AttributeContainer>
          <Header.Attribute label="for">{campaign.client.internal_name}</Header.Attribute>
          {campaign.payer.id !== campaign.client.id && (
            <Header.Attribute label="payer">{campaign.payer.internal_name}</Header.Attribute>
          )}
          {campaign.brand && <Header.Attribute label="brand">{campaign.brand?.title}</Header.Attribute>}
          {/* <Header.Attribute>{code}</Header.Attribute> */}
          <Header.Attribute>
            <StatusSelector
              status={status}
              size="sm"
              availableStatuses={available_statuses}
              onStatusChange={async (status) => {
                const res = await requestHandler<BlockDetails>({
                  url: 'status/',
                  method: 'POST',
                  body: { status },
                }).catch(actionErrorAndThrow);
                mutate(res, { revalidate: false });
              }}
            />
          </Header.Attribute>
          <Header.Attribute>
            <CommentDialogToggle topic={block.topic} />
          </Header.Attribute>
        </Header.AttributeContainer>
      }
      actions={<BlockActions campaign={campaign} />}
      menu={
        <MenuList variant="pills" direction="horizontal">
          <PopoverRoot>
            <PopoverTrigger openOnHover>
              <MenuItem component={NavLink} to="../planning" end label="Campaign" />
            </PopoverTrigger>
            <PopoverPopup placement="bottom-start">
              <CampaignMenu task_list={campaign.task_list} inBlock />
            </PopoverPopup>
          </PopoverRoot>
          <MenuItem label="Plan" component={NavLink} end to="" />
          <MenuItem
            label="Tasks"
            component={NavLink}
            to="tasks"
            suffix={task_list.opened ? <BBadge count={task_list.opened} /> : null}
          />
          <BriefButton />
          <MenuItem label="Agreement" component={NavLink} to="agreement" />
          <MenuItem label="History" component={NavLink} to="history" />
          <AttachmentButton endpoint={endpoint} />
        </MenuList>
      }
      metaAttributes={
        <Header.AttributeContainer>
          <Header.Attribute>{<ChannelSubscribers channelKey={channelKey} />}</Header.Attribute>
          <Header.Attribute>
            Manager <UserAvatar user={campaign.manager} size="xxs" />
          </Header.Attribute>
          <Header.Attribute>
            Planner{' '}
            <AvatarGroup size="xxs">
              {planners.map((user) => (
                <UserAvatar user={user} key={user.id} />
              ))}
            </AvatarGroup>
          </Header.Attribute>
          <Header.Attribute>
            <DeadlineDate deadline={deadline} />
          </Header.Attribute>
          <Header.Attribute>
            <PeriodWidget date_from={date_from} date_to={date_to} distance />
          </Header.Attribute>
        </Header.AttributeContainer>
      }
    />
  );
}
